import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { BrowserAuthError, InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import type { AuthUser, AppRole } from '../types/auth';
import { loginRequest, graphScopes, apiScopes, getRolesFromAccount, getLoginIdentities } from './authConfig';

/**
 * InteractionRequired: consent needed or session expired.
 * BrowserAuthError (timed_out): the silent iframe got no answer (Edge cookie restrictions, or before 29 Oct the
 * missing MSAL v5 redirect bridge). Both need an interactive sign-in.
 */
function needsInteraction(error: unknown): boolean {
  return (
    error instanceof InteractionRequiredAuthError ||
    (error instanceof BrowserAuthError && error.errorCode === 'timed_out')
  );
}

/**
 * Central auth hook wrapping MSAL React.
 *
 * Provides login/logout, token acquisition for Graph API,
 * role detection, and loading state.
 */
export function useAuth() {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const isLoading = inProgress !== InteractionStatus.None;

  const account = accounts[0] ?? null;
  const roles: AppRole[] = getRolesFromAccount(account);

  const identities = getLoginIdentities(account);

  const user: AuthUser | null = account
    ? {
        name: account.name ?? '',
        email: account.username ?? '',
        identities,
        roles,
        isAP: roles.includes('AP'),
        isCareManager: roles.includes('CareManager'),
        isAdmin: roles.includes('Admin'),
        accountId: account.homeAccountId,
      }
    : null;

  /** Sign in via redirect — navigates to Microsoft login and back */
  async function login(): Promise<void> {
    await instance.loginRedirect(loginRequest);
  }

  /** Sign out via redirect */
  async function logout(): Promise<void> {
    await instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  }

  /**
   * Acquire an access token for Microsoft Graph API.
   * Tries silent acquisition first; falls back to popup if interaction is required.
   */
  async function getGraphToken(): Promise<string> {
    if (!account) {
      throw new Error('No authenticated account. Call login() first.');
    }
    try {
      const response = await instance.acquireTokenSilent({
        ...graphScopes,
        account,
      });
      return response.accessToken;
    } catch (error) {
      // Popup first (a redirect navigates away and loses page state); a blocked popup falls back to the redirect.
      if (needsInteraction(error)) {
        console.warn('[getGraphToken] Silent acquisition failed, trying popup:', (error as Error).message);
        return interactiveToken(graphScopes.scopes);
      }
      throw error;
    }
  }

  /**
   * Popup first; if the browser blocks it (a popup only opens straight after a click, so a token that runs out while a
   * page loads its data always lands here), sign in on the full page instead. That comes back to this same page via
   * the redirect bridge, and its promise never settles because the page is leaving. A popup the user closed is
   * `user_cancelled` and is rethrown as before (QMS ISS-295).
   */
  async function interactiveToken(scopes: string[]): Promise<string> {
    try {
      const response = await instance.acquireTokenPopup({ scopes });
      return response.accessToken;
    } catch (error) {
      if (
        error instanceof BrowserAuthError &&
        (error.errorCode === 'popup_window_error' || error.errorCode === 'empty_window_error')
      ) {
        await instance.acquireTokenRedirect({ scopes, account: account ?? undefined });
        return new Promise<string>(() => {});
      }
      throw error;
    }
  }

  /**
   * Acquire an access token for the backend API (AP guest flows).
   * Silent first; popup fallback for Edge's blocked silent-iframe case —
   * same strategy as getGraphToken.
   */
  async function getApiToken(): Promise<string> {
    if (!account) {
      throw new Error('No authenticated account. Call login() first.');
    }
    try {
      const response = await instance.acquireTokenSilent({
        ...apiScopes,
        account,
      });
      return response.accessToken;
    } catch (error) {
      if (needsInteraction(error)) {
        console.warn('[getApiToken] Silent acquisition failed, trying popup:', (error as Error).message);
        return interactiveToken(apiScopes.scopes);
      }
      throw error;
    }
  }

  return {
    isAuthenticated,
    isLoading,
    user,
    roles,
    isAP: user?.isAP ?? false,
    isCareManager: user?.isCareManager ?? false,
    isAdmin: user?.isAdmin ?? false,
    login,
    logout,
    getGraphToken,
    getApiToken,
  };
}
